import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { mergeCartsAfterAuth } from '../lib/mergeCartsAfterAuth';
import { dispatchCartUpdate } from '../lib/pendingCart';
import { useToast } from '../hooks/use-toast';
import { isCartReturnState } from '../lib/cartNavigation';
import { passwordResetAPI } from '../lib/api';

const LoginPage = () => {
  const [mode, setMode] = useState('login'); // login | reset-email | reset-code | reset-password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const returnState = location.state?.from;

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setInfo('');
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error || 'Ошибка при входе');
      setLoading(false);
      return;
    }

      // Небольшая задержка, чтобы токен точно был установлен в localStorage
      await new Promise((resolve) => setTimeout(resolve, 250));

      try {
        const r = await mergeCartsAfterAuth();
        dispatchCartUpdate();

        const totalAdded = r.pending.ok + r.guest.ok;
        const totalFail = r.pending.fail + r.guest.fail;
        const hadWork = r.pending.total > 0 || r.guest.total > 0;

        if (hadWork) {
          if (totalFail === 0 && totalAdded > 0) {
            toast({
              title: 'Заказ добавлен в корзину',
              description:
                totalAdded === 1
                  ? 'Позиция перенесена в корзину'
                  : `Успешно добавлено позиций: ${totalAdded}`,
            });
          } else if (totalAdded > 0 && totalFail > 0) {
            toast({
              title: 'Частично добавлено',
              description: `Добавлено ${totalAdded}, не удалось перенести: ${totalFail}. Остальное можно добавить вручную.`,
              variant: 'default',
            });
          } else if (totalFail > 0) {
            toast({
              title: 'Ошибка',
              description:
                'Не удалось перенести выбранные позиции в корзину. Добавьте решение снова после входа.',
              variant: 'destructive',
            });
          }
        }

        const restoreStepNav = returnState?.restoreStep ?? returnState?.step ?? 1;
        if (returnState?.pathname === '/checkout') {
          navigate('/checkout', { state: { restoreStep: restoreStepNav } });
        } else if (isCartReturnState(returnState)) {
          navigate('/dashboard?tab=cart');
        } else if (hadWork) {
          navigate('/dashboard?tab=cart');
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error merging cart after login:', error);
        toast({
          title: 'Ошибка',
          description: error.message || 'Не удалось обновить корзину.',
          variant: 'destructive',
        });
        if (returnState?.pathname === '/checkout') {
          navigate('/checkout', { state: { restoreStep: returnState.restoreStep ?? 1 } });
        } else {
          navigate('/dashboard?tab=cart');
        }
      }
    setLoading(false);
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const data = await passwordResetAPI.requestCode(email.trim());
      setCode('');
      setMode('reset-code');
      setInfo(data.message || 'Если аккаунт с этой почтой есть, мы отправили код');
    } catch (err) {
      setError(err.message || 'Не удалось отправить код');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCodeStep = (e) => {
    e.preventDefault();
    setError('');
    const cleaned = code.trim().toUpperCase();
    if (cleaned.length !== 6) {
      setError('Введите код из 6 символов');
      return;
    }
    setCode(cleaned);
    setNewPassword('');
    setConfirmPassword('');
    switchMode('reset-password');
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('Пароль должен быть не короче 6 символов');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }
    setLoading(true);
    try {
      await passwordResetAPI.confirm({
        email: email.trim(),
        code: code.trim().toUpperCase(),
        new_password: newPassword,
      });
      toast({
        title: 'Пароль обновлён',
        description: 'Войдите с новым паролем',
      });
      setPassword('');
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      switchMode('login');
      setInfo('Пароль обновлён. Войдите с новым паролем');
    } catch (err) {
      setError(err.message || 'Не удалось сменить пароль');
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login: {
      title: 'Вход в личный кабинет',
      description: 'Введите ваши данные для входа',
    },
    'reset-email': {
      title: 'Восстановление доступа',
      description: 'Укажите почту — отправим код из 6 символов',
    },
    'reset-code': {
      title: 'Код из письма',
      description: `Введите код, отправленный на ${email || 'вашу почту'}`,
    },
    'reset-password': {
      title: 'Новый пароль',
      description: 'Придумайте новый пароль для входа',
    },
  };

  const heading = titles[mode] || titles.login;

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead title="Вход | Антология" noindex />
      <Header />
      <div className="flex-1 flex items-center justify-center px-4 py-16 pt-24">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">{heading.title}</CardTitle>
            <CardDescription>{heading.description}</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {info && !error && (
              <Alert className="mb-4">
                <AlertDescription>{info}</AlertDescription>
              </Alert>
            )}

            {mode === 'login' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Электронная почта</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="example@mail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="password">Пароль</Label>
                    <button
                      type="button"
                      className="text-sm text-accent hover:underline"
                      onClick={() => switchMode('reset-email')}
                    >
                      Забыли пароль?
                    </button>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Введите пароль"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Вход...' : 'Войти'}
                </Button>
              </form>
            )}

            {mode === 'reset-email' && (
              <form onSubmit={handleRequestCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Электронная почта</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="example@mail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Отправка...' : 'Получить код'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => switchMode('login')}
                >
                  Назад ко входу
                </Button>
              </form>
            )}

            {mode === 'reset-code' && (
              <form onSubmit={handleVerifyCodeStep} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-code">Код из письма</Label>
                  <Input
                    id="reset-code"
                    type="text"
                    inputMode="text"
                    autoComplete="one-time-code"
                    placeholder="Например, A7K3M2"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    maxLength={6}
                    required
                  />
                </div>
                <Button type="submit" className="w-full">
                  Далее
                </Button>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    disabled={loading}
                    onClick={handleRequestCode}
                  >
                    {loading ? 'Отправка...' : 'Отправить код ещё раз'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => switchMode('reset-email')}
                  >
                    Изменить почту
                  </Button>
                </div>
              </form>
            )}

            {mode === 'reset-password' && (
              <form onSubmit={handleConfirmReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Новый пароль</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Не менее 6 символов"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Повторите пароль</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Повторите пароль"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Сохранение...' : 'Сохранить пароль'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => switchMode('reset-code')}
                >
                  Назад к коду
                </Button>
              </form>
            )}

            {mode === 'login' && (
              <div className="mt-4 text-center text-sm">
                <span className="text-muted-foreground">Нет аккаунта? </span>
                <Link to="/register" state={{ from: returnState }} className="text-accent hover:underline">
                  Зарегистрироваться
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;
